import { Component, inject, OnInit, OnDestroy, AfterViewInit, HostListener } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { CommonModule } from '@angular/common';

import { LanguageSwitcherComponent } from '../components/language-switcher.component';
import { AuthActions } from '../state/auth/auth.actions';
import { ThemeService } from '../services/theme.service';
import { SharingService } from '../zk/sharing.service';
import { TestIds } from '@shared/test-ids';

declare var lucide: any;

@Component({
  selector: 'app-base-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, TranslateModule, LanguageSwitcherComponent],
  template: `
    <nav [class.collapsed]="isCollapsed">
        <div class="logo-area" style="display: flex; align-items: center; gap: 12px;">
            <div class="logo-icon" (click)="toggleSidebar()">
                <i data-lucide="wind" size="20"></i>
            </div>
            <h2 class="nav-text" style="font-size: 1.25rem; font-weight: 800; letter-spacing: 0.5px; margin: 0;">SMART</h2>
        </div>
        
        <div class="nav-text nav-switcher-container">
            <app-language-switcher></app-language-switcher>
            <button class="theme-toggle-btn" [attr.data-testid]="TestIds.Nav.ThemeToggleButton" (click)="themeService.toggle()" [title]="themeService.isDark() ? 'Switch to Light Mode' : 'Switch to Dark Mode'">
                <svg *ngIf="themeService.isDark()" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-sun"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>
                <svg *ngIf="!themeService.isDark()" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-moon"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>
            </button>
        </div>
        <ul class="nav-links">
            <li class="nav-text" style="color: var(--text-muted); font-size: 0.75rem; font-weight: 700; text-transform: uppercase; padding: 12px 16px; margin-top: 1rem;">{{ 'NAV.CUSTOMERS' | translate }}</li>
            <a routerLink="/customers" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.CustomersLink"><i data-lucide="user-check" size="18"></i> <span class="nav-text">{{ 'NAV.CUSTOMERS' | translate }}</span></a>
            <a routerLink="/partners" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.PartnersLink"><i data-lucide="briefcase" size="18"></i> <span class="nav-text">{{ 'NAV.PARTNERS' | translate }}</span></a>
            <a routerLink="/fleet" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.FleetLink"><i data-lucide="truck" size="18"></i> <span class="nav-text">{{ 'NAV.FLEET' | translate }}</span></a>
            <a routerLink="/warehouses" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.WarehousesLink"><i data-lucide="warehouse" size="18"></i> <span class="nav-text">{{ 'NAV.WAREHOUSES' | translate }}</span></a>
            <a routerLink="/inventory" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.InventoryLink"><i data-lucide="package" size="18"></i> <span class="nav-text">{{ 'NAV.INVENTORY' | translate }}</span></a>

            <li class="nav-text" style="color: var(--text-muted); font-size: 0.75rem; font-weight: 700; text-transform: uppercase; padding: 12px 16px; margin-top: 1rem;">{{ 'NAV.OPERATIONS' | translate }}</li>
            <a routerLink="/orders" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.OrdersLink"><i data-lucide="file-text" size="18"></i> <span class="nav-text">{{ 'NAV.ORDERS' | translate }}</span></a>
            <a routerLink="/work-orders" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.WorkOrdersLink"><i data-lucide="calendar" size="18"></i> <span class="nav-text">{{ 'NAV.WORK_ORDERS' | translate }}</span></a>
            <a routerLink="/work-orders/dispatch" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.DispatchBoardLink"><i data-lucide="calendar-plus" size="18"></i> <span class="nav-text">{{ 'NAV.DISPATCH_BOARD' | translate }}</span></a>
            <a routerLink="/visits" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.VisitsLink"><i data-lucide="map" size="18"></i> <span class="nav-text">{{ 'NAV.VISITS' | translate }}</span></a>
            <a routerLink="/devices" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.DevicesLink"><i data-lucide="thermometer" size="18"></i> <span class="nav-text">{{ 'NAV.DEVICES' | translate }}</span></a>

            <li class="nav-text" style="color: var(--text-muted); font-size: 0.75rem; font-weight: 700; text-transform: uppercase; padding: 12px 16px; margin-top: 1rem;">{{ 'NAV.LOOKUPS' | translate }}</li>
            <a routerLink="/lookups/areas" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.AreasLink"><i data-lucide="map-pin" size="18"></i> <span class="nav-text">{{ 'NAV.AREAS' | translate }}</span></a>
            <a routerLink="/lookups/complaint-types" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.ComplaintTypesLink"><i data-lucide="alert-triangle" size="18"></i> <span class="nav-text">{{ 'NAV.COMPLAINT_TYPES' | translate }}</span></a>
            <a routerLink="/lookups/refrigerants" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.RefrigerantsLink"><i data-lucide="droplets" size="18"></i> <span class="nav-text">{{ 'NAV.REFRIGERANTS' | translate }}</span></a>
            <a routerLink="/lookups/models" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.ModelsLink"><i data-lucide="box" size="18"></i> <span class="nav-text">{{ 'NAV.MODELS' | translate }}</span></a>
            <a routerLink="/lookups/usual-feedbacks" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.UsualFeedbacksLink"><i data-lucide="message-square" size="18"></i> <span class="nav-text">{{ 'NAV.USUAL_FEEDBACK' | translate }}</span></a>

            <li class="nav-text" style="color: var(--text-muted); font-size: 0.75rem; font-weight: 700; text-transform: uppercase; padding: 12px 16px; margin-top: 1rem;">{{ 'NAV.FINANCE' | translate }}</li>
            <a routerLink="/finance/sales" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.SalesLink"><i data-lucide="shopping-cart" size="18"></i> <span class="nav-text">{{ 'NAV.SALES' | translate }}</span></a>
            <a routerLink="/finance/invoices" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.InvoicesLink"><i data-lucide="receipt" size="18"></i> <span class="nav-text">{{ 'NAV.INVOICES' | translate }}</span></a>
            <a routerLink="/finance/cash-positions" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.CashLink"><i data-lucide="wallet" size="18"></i> <span class="nav-text">{{ 'NAV.CASH' | translate }}</span></a>
            <a routerLink="/finance/transfers" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.TransfersLink"><i data-lucide="arrow-left-right" size="18"></i> <span class="nav-text">{{ 'NAV.TRANSFERS' | translate }}</span></a>
            <a routerLink="/finance/spending" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.SpendingLink"><i data-lucide="credit-card" size="18"></i> <span class="nav-text">{{ 'NAV.SPENDING' | translate }}</span></a>
            <a routerLink="/finance/vat-config" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.VatConfigLink"><i data-lucide="percent" size="18"></i> <span class="nav-text">{{ 'NAV.VAT' | translate }}</span></a>

            <li class="nav-text" style="color: var(--text-muted); font-size: 0.75rem; font-weight: 700; text-transform: uppercase; padding: 12px 16px; margin-top: 1rem;">{{ 'NAV.ADMIN' | translate }}</li>
            <a routerLink="/admin/users" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.UsersLink"><i data-lucide="users" size="18"></i> <span class="nav-text">{{ 'NAV.USERS' | translate }}</span></a>
            <a routerLink="/admin/roles" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.RolesLink"><i data-lucide="shield" size="18"></i> <span class="nav-text">{{ 'NAV.ROLES' | translate }}</span></a>
            <a routerLink="/settings/tenants" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.TenantsLink"><i data-lucide="building-2" size="18"></i> <span class="nav-text">{{ 'NAV.TENANTS' | translate }}</span></a>

            <li class="nav-text" style="color: var(--text-muted); font-size: 0.75rem; font-weight: 700; text-transform: uppercase; padding: 12px 16px; margin-top: 1rem;">{{ 'NAV.SECURITY' | translate }}</li>
            <a routerLink="/settings/zk-tenant-setup" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.ZkTenantSetupLink"><i data-lucide="shield-check" size="18"></i> <span class="nav-text">{{ 'NAV.ZK_TENANT_SETUP' | translate }}</span></a>
            <a routerLink="/settings/zk-enrollment" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.ZkEnrollmentLink"><i data-lucide="key-round" size="18"></i> <span class="nav-text">{{ 'NAV.ZK_ENROLLMENT' | translate }}</span></a>
            <a routerLink="/settings/zk-access" routerLinkActive="active" class="nav-item" [attr.data-testid]="TestIds.Nav.ZkAccessLink">
                <i data-lucide="users" size="18"></i> <span class="nav-text">{{ 'NAV.ZK_ACCESS' | translate }}</span>
                @if (sharing.pendingCount() > 0) {
                    <span class="nav-badge" [attr.data-testid]="TestIds.Nav.ZkAccessBadge">{{ sharing.pendingCount() }}</span>
                }
            </a>
        </ul>

        <div class="system-status-container" style="margin-top: auto; padding-top: 1rem;">
            <div style="margin: 0 16px 1.5rem 16px; background: rgba(255,255,255,0.05); padding: 10px 16px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.08); display: flex; align-items: center; gap: 10px;">
                <div style="width: 8px; height: 8px; background: var(--accent-mint); border-radius: 50%; box-shadow: 0 0 8px var(--accent-mint);"></div>
                <span style="font-size: 0.85rem; font-weight: 700; color: white;">SYSTEM ONLINE</span>
            </div>
            <div class="nav-item" style="border-top: 1px solid rgba(255,255,255,0.08); padding-top: 1.5rem; margin-bottom: 0;" [attr.data-testid]="TestIds.Nav.LogoutButton" (click)="logout()">
                <i data-lucide="log-out" size="18"></i> <span class="nav-text">{{ 'AUTH.LOGOUT' | translate }}</span>
            </div>
        </div>
    </nav>

    <main>
        <router-outlet></router-outlet>
    </main>
  `,
  styles: [`
    a { text-decoration: none; }
  `]
})
export class BaseLayoutComponent implements AfterViewInit, OnInit, OnDestroy {
  protected readonly TestIds = TestIds;
  isCollapsed = false;
  private store = inject(Store);
  private router = inject(Router);
  public themeService = inject(ThemeService);
  protected sharing = inject(SharingService);

  // Nav badge (Phase F) — polled rather than pushed, since this app has no websocket/SSE
  // infra. 60s is a reasonable balance for a low-urgency internal-tool notification.
  private static readonly PENDING_COUNT_POLL_MS = 60_000;
  private pendingCountPollId?: ReturnType<typeof setInterval>;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    // Only auto-collapse when screen size is small.
    // When expanding window to desktop size, we can restore it.
    if (window.innerWidth <= 768) {
      if (!this.isCollapsed) {
        this.isCollapsed = true;
      }
    }
  }

  ngOnInit() {
    this.isCollapsed = window.innerWidth <= 768;
    void this.sharing.refreshPendingCount();
    this.pendingCountPollId = setInterval(
      () => void this.sharing.refreshPendingCount(),
      BaseLayoutComponent.PENDING_COUNT_POLL_MS,
    );
  }

  ngOnDestroy() {
    if (this.pendingCountPollId) clearInterval(this.pendingCountPollId);
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    // Re-initialize icons shortly after animation to ensure they render properly if needed
    setTimeout(() => {
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }, 350);
  }

  ngAfterViewInit() {
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Auto-collapse sidebar on mobile when navigating
        if (window.innerWidth <= 768 && !this.isCollapsed) {
            this.isCollapsed = true;
        }

        void this.sharing.refreshPendingCount();

        setTimeout(() => {
          if (typeof lucide !== 'undefined') {
            lucide.createIcons();
          }
        }, 50);
      }
    });
  }

  logout() {
    this.store.dispatch(AuthActions.logout());
  }
}
